import { z } from "zod";
import {
    useForm,
    FormProvider,
    useFormContext,
    Controller,
    useFieldArray,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";
import LoaderOverlay from "@/components/common/LoaderOverlay";
import { MILESTONE_STATUS_WITH_KEY } from "@/utils/constant";
import { contractDetails, milestone, } from "@/store/contractStore";
import { authHeaders, Axios } from "@/utils/axios";
import { API_END_POINTS } from "@/utils/routes";

const mileStoneSchema = z.object({
    milestones: z
        .array(
            z.object({
                _id: z.string().trim().optional(),
                contractId: z.string().trim().optional(),
                title: z.string().trim().min(1, "Title is required"),
                amount: z.string()
                    .trim()
                    .min(1, "Amount is required")
                    .refine((val) => Number(val) >= 0, {
                        message: "Amount value cannot be negative",
                    }),
                status: z.string().trim().min(1, "Status is required"),
                startDate: z
                    .string()
                    .trim()
                    .min(1, "Start date is required")
                    .transform((val) => new Date(val).toISOString()),
                dueDate: z
                    .string()
                    .trim()
                    .min(1, "End date is required")
                    .transform((val) => new Date(val).toISOString()),
                description: z.string().trim().optional(),
            })
        )
        .optional()
});

type FormData = z.infer<typeof mileStoneSchema>;
const jobApplicationStatus = MILESTONE_STATUS_WITH_KEY;

interface ContractDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    loadContractData: () => void;
    updateContractData?: contractDetails | null;
}

export default function Milestone({
    open,
    onOpenChange,
    loadContractData,
    updateContractData
}: ContractDialogProps) {
    const methods = useForm<FormData>({
        resolver: zodResolver(mileStoneSchema),
        defaultValues: {
            milestones: [],
        },
        mode: "onSubmit",
    });
    const { toast } = useToast();
    const { user } = useUserStore();
    const [isLoading, setIsLoading] = useState(false);
    const { reset } = methods;
    const handleOpenChange = (isOpen: boolean) => {
        methods.reset();
        onOpenChange(isOpen);
    };

    useEffect(() => {
        methods.reset();
        if (updateContractData) {
            reset({
                milestones: updateContractData && updateContractData?.milestones && updateContractData?.milestones?.length ? patchMileStoneValue(updateContractData?.milestones) : [],
            })
        }
    }, [updateContractData, reset]);

    const patchMileStoneValue = (milestones: milestone[]): milestone[] => {
        const finalMilestones = milestones.map(m => ({
            ...m,
            startDate: m.startDate
                ? new Date(m.startDate).toISOString().split("T")[0]
                : "",
            dueDate: m.dueDate
                ? new Date(m.dueDate).toISOString().split("T")[0]
                : "",
            amount: m.amount?.toString(),
            status: m.status?.toString()
        }));
        return finalMilestones;
    };
    const onSubmit = async (data: FormData) => {
        if (isLoading || !data?.milestones?.length) {
            return;
        }
        setIsLoading(true);
        let milestoneReq = data?.milestones?.length
            ? data.milestones.map(m => {
                const data = {
                    ...m,
                    amount: Number(m.amount) || 0,
                    status: Number(m.status) || 0,
                };
                if (!data._id) {
                    delete data._id;
                }
                return data;
            })
            : null;

        try {
            const response = milestoneReq[0]?._id ? await Axios.put(
                `${API_END_POINTS.ADD_UPDATE_MILESTONE}/${milestoneReq[0]?._id}`,
                milestoneReq[0],
                authHeaders(user?.accessToken)
            ) : await Axios.post(
                `${API_END_POINTS.ADD_UPDATE_MILESTONE}`,
                milestoneReq[0],
                authHeaders(user?.accessToken)
            );
            if (response) {
                setIsLoading(false);
                toast({
                    title: milestoneReq[0]?._id ? "Milestone Updated!" : "Milestone Created!",
                    description: milestoneReq[0]?._id ? "Milestone Updated successfully." : "Milestone Created successfully.",
                });
            }
            loadContractData()
        } catch (error) {
            setIsLoading(false);
            toast({
                title: "Error",
                description: error && error?.response && error?.response?.data?.error ? error?.response?.data?.error :
                    milestoneReq[0]?._id ? "Milestone Updated successfully." : "Milestone Created successfully.",
                variant: "destructive",
            });
        }
        methods.reset();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] pt-7 pb-7">
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <DialogHeader>
                            <DialogTitle>{updateContractData?.milestones[0]?._id ? 'Update Milestone' : 'Add Milestone' }</DialogTitle>
                        </DialogHeader>
                        {isLoading && <LoaderOverlay />}
                        <ContractFormContent isLoading={isLoading} updateContractData={updateContractData} />
                        <DialogFooter>
                            <Button
                                variant="outline"
                                type="button"
                                disabled={isLoading}
                                onClick={() => handleOpenChange(false)}
                            >
                                Close
                            </Button>
                            <Button type="submit" disabled={isLoading}>{updateContractData?.milestones[0]?._id ? 'Update Milestone' : 'Add Milestone' }</Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
}

interface ContractForm {
    isLoading: boolean;
    updateContractData?: contractDetails | null;
}

function ContractFormContent({ isLoading, updateContractData }: ContractForm) {
    const {
        register,
        control,
        formState: { errors },
        watch,
        setValue,
        getValues 
    } = useFormContext<FormData>();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "milestones",
    });
    const [mileStoneStart, setMileStoneStart] = useState(null);


    const disableStatusOption = (status: string | number) => {
        const disabledStatus = ["1", "2", "3", ]
        return disabledStatus?.includes(status?.toString()) ? true : false ;
    }

    const start = updateContractData?.startDate ? new Date(updateContractData?.startDate)?.toISOString()?.split("T")[0]
    : new Date()?.toISOString()?.split("T")[0];
    const end = updateContractData?.startDate ? new Date(updateContractData?.endDate)?.toISOString()?.split("T")[0] :
    new Date()?.toISOString()?.split("T")[0];

    useEffect(() => {
        if (!start) return;

        fields.forEach((_, index) => {
            const milestoneStart = getValues(`milestones.${index}.startDate`);
            if (milestoneStart && milestoneStart < start) {
                // clear or reset to contract start
                setValue(`milestones.${index}.startDate`, start, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
        });
    }, [start, fields, getValues, setValue]);

    useEffect(() => {
        if (!end) return;
        fields.forEach((_, index) => {
            const milestoneDueDate = getValues(`milestones.${index}.dueDate`);
            if (milestoneDueDate && milestoneDueDate > end) {
                // clear or reset to contract start
                setValue(`milestones.${index}.dueDate`, end, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
            if (milestoneDueDate && milestoneDueDate < mileStoneStart) {
                const newDate = addDays(mileStoneStart, 1)
                setValue(`milestones.${index}.dueDate`, newDate, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            }
        });
    }, [end, mileStoneStart, fields, getValues, setValue]);

    const toDateInputValue = (date?: string | Date) => {
        if (!date) return undefined;
        const d = typeof date === "string" ? new Date(date) : date;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const addDays = (dateStr: string, days: number) => {
        if (!dateStr) return dateStr;
        const [y, m, d] = dateStr.split("-").map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + days);
        const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(dt.getUTCDate()).padStart(2, "0");
        return `${dt.getUTCFullYear()}-${mm}-${dd}`;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "3":
                return "bg-emerald-100 text-emerald-800";
            case "7":
                return "bg-blue-100 text-blue-800";
            case "4":
                return "bg-blue-100 text-blue-800";
            case "1":
                return "bg-gray-100 text-gray-800";
            case "2":
                return "bg-amber-100 text-amber-800";
            case "6":
                return "bg-red-100 text-red-800";
            case "5":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    }

    return (
        <div className="grid gap-4 py-5">
            {/* Milestones */}
            {fields.map((field, index) => (<div key={field.id} className="grid grid-cols-4 gap-4 items-start">
                <div className="col-span-4 space-y-4">
                    <div
                        className="relative  p-4 space-y-3 pr-10 pl-5"
                    >
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Title <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Input
                                    placeholder="Milestone title"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.title`)}
                                />
                                {errors.milestones?.[index]?.title && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.title?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Amount */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Amount <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Input
                                    type="number"
                                    placeholder="0.00"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.amount`)}
                                />
                                {errors.milestones?.[index]?.amount && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.amount?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Status */}
                        {updateContractData?.milestones[0]?._id && <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Status <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Controller
                                    control={control}
                                    name={`milestones.${index}.status`}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value?.toString()}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger>
                                                <Badge className={getStatusColor(field?.value?.toString())}>
                                                    {field.value
                                                        ? jobApplicationStatus.find((s) => s.value?.toString() === field.value?.toString())?.label
                                                        : "Select status"}
                                                </Badge>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {jobApplicationStatus.map((status) => (
                                                    <SelectItem
                                                        key={status.value}
                                                        value={status.value}
                                                        disabled={disableStatusOption(status.value)}
                                                    >
                                                        {status.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                                {errors.milestones?.[index]?.status && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.status?.message}
                                    </p>
                                )}
                            </div>
                        </div>}
                        {/* Start Date */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Start Date <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Input
                                    type="date"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.startDate`, {
                                        onChange: (e) => {
                                            setMileStoneStart(e.target.value);
                                        },
                                    })}

                                    min={toDateInputValue(start) || new Date().toISOString().split("T")[0]}
                                    max={toDateInputValue(end) || undefined}
                                />
                                {errors.milestones?.[index]?.startDate && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.startDate?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Due Date */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">
                                Due Date <span className="text-red-500">*</span>
                            </Label>
                            <div className="col-span-3">
                                <Input
                                    type="date"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.dueDate`)}
                                    min={
                                        watch(`milestones.${index}.startDate`)
                                            ? toDateInputValue(addDays(watch(`milestones.${index}.startDate`), 1))
                                            : start
                                                ? toDateInputValue(addDays(start, 1))
                                                : undefined
                                    }
                                    max={toDateInputValue(end)}
                                />
                                {errors.milestones?.[index]?.dueDate && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.milestones[index]?.dueDate?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        {/* Description */}
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right col-span-1">Description</Label>
                            <div className="col-span-3">
                                <Textarea
                                    placeholder="Add description"
                                    className="col-span-3"
                                    {...register(`milestones.${index}.description`)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>))}
        </div>
    );
}
